import React, { useState, useRef, useEffect } from "react";

const CustomTreeSelect = ({
  options = [],
  value: controlledValue,
  defaultValue,
  onChange,
  placeholder = "Please select",
  width = "230px",
  fontSize = "14px",
  disabled = false,
  disabledItemValues,
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  // Recursive function to find selected item in nested structure
  const findSelectedItem = (items, val) => {
    for (const item of items) {
      if (item.value === val) return item;
      if (item.children) {
        const found = findSelectedItem(item.children, val);
        if (found) return found;
      }
    }
    return null;
  };

  const selectedItem = findSelectedItem(options, value);

  const handleSelect = (item, isItemDisabled) => {
  // Don't select if the item is disabled
  if (isItemDisabled) return;

  if (controlledValue === undefined) {
    setInternalValue(item.value);
  }
  setIsOpen(false);
  onChange?.(item.value, item); // Pass both value and full item object
};

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Recursive function to render tree items
  const renderTreeItems = (items, level = 0) => {
    return items.map((item) => {
      // Check if item should be disabled using the custom function
      const isItemDisabled = disabledItemValues
        ? disabledItemValues(item)
        : false;

      return (
        <React.Fragment key={item.value}>
          <div
            onClick={() => handleSelect(item, isItemDisabled)}
            style={{
              padding: "8px 12px",
              paddingLeft: `${12 + level * 20}px`,
              cursor: disabled || isItemDisabled ? "not-allowed" : "pointer",
              fontSize,
              color: disabled || isItemDisabled ? "#00000040" : "#000",
              backgroundColor: item.value === value ? "#e6f4ff" : "white",
              transition: "background-color 0.2s",
              opacity: isItemDisabled ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (item.value !== value && !disabled && !isItemDisabled) {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                item.value === value ? "#e6f4ff" : "white";
            }}
          >
            {item.title}
          </div>
          {item.children && renderTreeItems(item.children, level + 1)}
        </React.Fragment>
      );
    });
  };

  return (
    <div
      ref={containerRef}
      style={{
        width,
        fontFamily: "system-ui, -apple-system, sans-serif",
        position: "relative",
      }}
    >
      {/* Selector */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          padding: "8px 12px",
          backgroundColor: disabled ? "#f5f5f5" : "white",
          border: "none",
          borderBottom: isOpen ? "2px solid #1890ff" : "1px solid #d9d9d9",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize,
          color: disabled ? "#00000040" : "#000",
          transition: "border-bottom 0.2s",
        }}
      >
        <span>{selectedItem?.title || placeholder}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <path
            d="M2 4L6 8L10 4"
            stroke={disabled ? "#00000040" : "#00000073"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          style={{
            position: "absolute",
            backgroundColor: "white",
            border: "1px solid #d9d9d9",
            borderTop: "none",
            width,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            zIndex: 1000,
            marginTop: "0px",
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {renderTreeItems(options)}
        </div>
      )}
    </div>
  );
};

export default CustomTreeSelect;
